import { useCallback, useEffect, useState } from 'react';
import {
  runDiagnostics,
  siteHealth,
  type ConnectionDiagnostics,
  type SiteHealth,
} from '../../lib/health-api';
import { fmtTime, HealthBadge, IssueList, SummaryChips } from './HealthParts';
import styles from './admin.module.css';

// Integration health for one site: a verdict per connection with the reasons,
// delivery stats, and a "Diagnose" action that probes the provider and lists
// recent failures and dead jobs.
export function ChannelHealthPanel({ siteId, refreshKey }: { siteId: string; refreshKey: string }) {
  const [data, setData] = useState<SiteHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diag, setDiag] = useState<Record<string, ConnectionDiagnostics>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await siteHealth(siteId));
      setError(null);
    } catch {
      setError('Could not load integration health.');
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function diagnose(connectionId: string) {
    setBusy(connectionId);
    try {
      const d = await runDiagnostics(siteId, connectionId);
      setDiag((m) => ({ ...m, [connectionId]: d }));
      await load();
    } catch {
      setError('Diagnostics failed.');
    } finally {
      setBusy(null);
    }
  }

  if (error) return <p className={styles.error} role="alert">{error}</p>;
  if (!data || data.connections.length === 0) return null;

  return (
    <div style={{ marginBottom: 'var(--inic-space-4)' }}>
      <h3 className={styles.label}>Integration health</h3>
      <SummaryChips summary={data.summary} />
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>Connection</th><th>Health</th><th>Last 24 h</th><th>Last message in / out</th><th /></tr>
          </thead>
          <tbody>
            {data.connections.map(({ channel, health }) => {
              const d = diag[channel.id];
              const s = health.stats;
              return (
                <tr key={channel.id}>
                  <td>
                    {channel.name} <span className={styles.badgeMuted}>{channel.channel}</span>
                  </td>
                  <td>
                    <HealthBadge state={health.state} />
                    <IssueList health={health} />
                    {d && (
                      <div style={{ marginTop: 'var(--inic-space-2)' }}>
                        <strong className={styles.hint}>Diagnostics · {fmtTime(d.generatedAt)}</strong>
                        <ul className={styles.issueList} style={{ listStyle: 'none', paddingLeft: 0 }}>
                          {d.steps.map((st) => (
                            <li key={st.name}>
                              <span className={st.ok ? styles.stepOk : styles.stepFail}>{st.ok ? '✓' : '✗'}</span> {st.name} — {st.detail}
                            </li>
                          ))}
                        </ul>
                        {d.recentFailures.length > 0 && (
                          <>
                            <strong className={styles.hint}>Recent failed replies</strong>
                            <ul className={styles.issueList}>
                              {d.recentFailures.map((f) => (
                                <li key={f.messageId}>
                                  {fmtTime(f.at)} · {f.status}: {f.error ?? 'no reason recorded'}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {d.deadJobs.length > 0 && (
                          <>
                            <strong className={styles.hint}>Dead jobs (replay from Admin → Jobs)</strong>
                            <ul className={styles.issueList}>
                              {d.deadJobs.map((j) => (
                                <li key={j.id}>
                                  {j.queue} · {j.attempts} attempts: {j.lastError ?? 'no error recorded'}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {s.inbound24h} in · {s.ok24h} sent
                    {s.failed24h > 0 && <> · <span className={styles.stepFail}>{s.failed24h} failed</span></>}
                    {s.queued > 0 && <> · {s.queued} queued</>}
                  </td>
                  <td className={styles.hint} style={{ whiteSpace: 'nowrap' }}>
                    {fmtTime(s.lastInboundAt)}<br />{fmtTime(s.lastOutboundAt)}
                  </td>
                  <td>
                    {channel.status !== 'disconnected' && (
                      <button type="button" className={styles.buttonGhost} disabled={busy === channel.id} onClick={() => void diagnose(channel.id)}>
                        {busy === channel.id ? 'Running…' : 'Diagnose'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span className={styles.hint}>Updated {fmtTime(data.generatedAt)}. “Last message” looks back {data.connections[0]?.health.stats.windowDays ?? 7} days.</span>
    </div>
  );
}
